import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { getESignatureSummary } from "@/features/esignatures/api";
import type { SummaryGroup } from "@/features/esignatures/types";
import { formatShortDate } from "@/lib/format";

const LAST_VIEW_KEY = "esignatures:lastView";

type SortKey = "client" | "oldest" | "count";

/** Firm-wide pending-signature summary grouped by client. Renders bare (no
 * Card) so it can back both the standalone dashboard card
 * (`DashboardESignatures`) and the Control Center E-Signatures tab. "New since
 * last visit" is driven by a per-browser last-view timestamp. */
export function ESignatureSummaryFeed() {
  const [groups, setGroups] = useState<SummaryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "oldest",
    dir: "asc",
  });

  useEffect(() => {
    let active = true;
    const since = localStorage.getItem(LAST_VIEW_KEY) ?? undefined;
    getESignatureSummary({ scope: "firm", since })
      .then((summary) => {
        if (!active) return;
        setGroups(summary.groups);
        // Only advance the "last viewed" marker on a successful load, so a
        // failed fetch can't silently swallow the "new" window.
        localStorage.setItem(LAST_VIEW_KEY, new Date().toISOString());
      })
      .catch(() => active && setError("Couldn't load e-signature summary."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const sorted = useMemo(() => {
    const rows = [...groups];
    const dir = sort.dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      let cmp = 0;
      if (sort.key === "client") cmp = a.clientName.localeCompare(b.clientName);
      else if (sort.key === "count") cmp = a.pendingCount - b.pendingCount;
      else cmp = a.oldestPending.localeCompare(b.oldestPending);
      return cmp * dir;
    });
    return rows;
  }, [groups, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader />
      </div>
    );
  }
  if (error) {
    return <p className="text-destructive p-4 text-sm">{error}</p>;
  }
  if (sorted.length === 0) {
    return (
      <p className="text-muted-foreground p-4 text-sm">No pending signature requests.</p>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <button type="button" onClick={() => toggleSort("client")}>
              Client
            </button>
          </TableHead>
          <TableHead>
            <button type="button" onClick={() => toggleSort("oldest")}>
              Oldest pending
            </button>
          </TableHead>
          <TableHead className="text-right">
            <button type="button" onClick={() => toggleSort("count")}>
              Pending
            </button>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((g) => (
          <TableRow key={g.client}>
            <TableCell>
              <Link
                to={`/client/${g.client}/esignatures`}
                className="text-foreground hover:text-primary font-medium hover:underline"
              >
                {g.clientName}
              </Link>
              {g.newCount > 0 && (
                <Badge variant="destructive" className="ml-2 px-1.5 text-[10px]">
                  {g.newCount} new
                </Badge>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatShortDate(g.oldestPending)}
            </TableCell>
            <TableCell className="text-right font-semibold">{g.pendingCount}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
